import { z } from 'zod';
/**
 * Payload shape for `GET /api/dashboard/pending-payouts`.
 *
 * Surfaces the operator's next bank payout from Stripe — money
 * Stripe is holding in the Connect account that hasn't yet been
 * paid out to the operator's external bank account on the
 * configured payout schedule.
 *
 * Important distinction: this is Stripe's `Payout` object (bank
 * payout aggregate), NOT EVI's local `PayoutModel` (per-session
 * revenue split). The two share the name and represent very
 * different things. The widget surfaces the Stripe notion
 * because that's what an operator means by "when's my next
 * payout."
 *
 * Scope
 * ─────
 * Operator-tier widget. tenant_admin / super_admin / operator /
 * viewer all see the tenant's single Connect-account aggregate.
 * third_party_admin gets `widget_applies: false` so the frontend
 * self-hides — that role's "what does EVI owe my building"
 * question is the Settlement Summary widget (Phase D #10), not
 * this one. Connect accounts are stored at Tenant grain
 * (`Tenant.stripe_connect_account_id`), so a Building Admin
 * scoped to one location couldn't get a meaningful per-location
 * slice of a tenant-wide bank payout anyway.
 */
export const PendingPayoutsResponseSchema = z.object({
    /** False for third_party_admin (and any future ineligible
     *  caller). The frontend renders nothing — no empty card, no
     *  skeleton — when this is false. */
    widget_applies: z.boolean(),
    /** False when the tenant hasn't set up a Stripe Connect
     *  account yet. Distinct from "configured but no pending
     *  payouts." Drives the empty-state copy. */
    has_connect_account: z.boolean(),
    /** Sum of pending payout amounts in the smallest currency
     *  unit (cents for USD). */
    total_amount_cents: z.number().int().nonnegative(),
    /** Number of pending Stripe Payout objects. Usually 1 — Stripe
     *  groups payouts by schedule cycle — but can be higher when
     *  multiple currencies or schedules are in play. */
    count: z.number().int().nonnegative(),
    /** ISO date of the soonest expected arrival. Null when count=0. */
    earliest_arrival_date: z.string().nullable(),
    /** ISO 4217 currency of the pending payout. When multiple
     *  currencies are pending this picks the largest by amount;
     *  most operators are single-currency so this is practically
     *  always 'USD'. */
    currency: z.string(),
    /** Deep-link to the Connect account's payouts tab in Stripe
     *  Dashboard. Null when no Connect account is configured.
     *  Renders behind a `target="_blank" rel="noopener noreferrer"`
     *  link. Most operators don't have Stripe Dashboard access;
     *  the link is for those who do. */
    stripe_dashboard_payouts_url: z.string().nullable(),
    generated_at: z.string(),
});
//# sourceMappingURL=dashboard-pending-payouts.js.map